import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'

import {useInvalidateJobChange} from '../jobs/use-jobs'
import {
    getDispatchAttentionCount,
    getDispatchBoard,
    listDispatchYards,
    setProjectDispatchYard,
} from './api'
import {dispatchKeys} from './query-keys'

export function useDispatchAttentionCount(options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: dispatchKeys.attentionCount(),
        queryFn: getDispatchAttentionCount,
        enabled: options?.enabled ?? true,
    })
}

export function useSyncDispatchAttentionCountFromBoard() {
    const queryClient = useQueryClient()

    return useCallback(() => {
        void queryClient.invalidateQueries({queryKey: dispatchKeys.attentionCount()})
    }, [queryClient])
}

export function useInvalidateDispatchAttentionCount() {
    const queryClient = useQueryClient()

    return useCallback(() => {
        void queryClient.invalidateQueries({queryKey: dispatchKeys.attentionCount()})
    }, [queryClient])
}

export function useDispatchYards() {
    return useQuery({
        queryKey: dispatchKeys.yards(),
        queryFn: listDispatchYards,
        staleTime: Infinity,
    })
}

export function useSetProjectDispatchYard() {
    const queryClient = useQueryClient()

    const invalidateJobChange = useInvalidateJobChange()

    return useMutation({
        mutationFn: ({projectId, dispatchYardId}: {projectId: string; dispatchYardId: string}) =>
            setProjectDispatchYard(projectId, dispatchYardId),
        onSuccess: (_data, {projectId}) => {
            invalidateJobChange(projectId)
            void queryClient.invalidateQueries({queryKey: dispatchKeys.attentionCount()})
        },
    })
}

export function useDispatchBoard(startDate: string, endDate: string) {
    return useQuery({
        queryKey: dispatchKeys.board(startDate, endDate),
        queryFn: () => getDispatchBoard({startDate, endDate}),
        staleTime: 60_000,
    })
}
